/**
 * CategoryManagementDialog Component
 *
 * Dialog for managing categories (CRUD operations).
 * Features:
 * - List all categories with color swatches
 * - Create/edit/delete categories
 * - Color picker integration
 * - Icon selector from lucide-react
 * - Show class count per category
 */

import React, { useState, useEffect } from 'react'
import { HexColorPicker } from 'react-colorful'
import {
  X,
  Plus,
  Edit2,
  Trash2,
  Circle,
  Square,
  Triangle,
  Star,
  Heart,
  Diamond,
  Package,
  Check,
  Palette,
} from 'lucide-react'
import { Category } from '@/types'
import { useCategoryContext } from '@/contexts/CategoryContext'
import { apiService } from '@/services/api'

interface CategoryManagementDialogProps {
  isOpen: boolean
  onClose: () => void
}

// Available icons for categories
const AVAILABLE_ICONS = [
  { name: 'circle', Icon: Circle },
  { name: 'square', Icon: Square },
  { name: 'triangle', Icon: Triangle },
  { name: 'star', Icon: Star },
  { name: 'heart', Icon: Heart },
  { name: 'diamond', Icon: Diamond },
  { name: 'package', Icon: Package },
]

// Preset colors for quick selection
const PRESET_COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#EAB308', // Yellow
  '#22C55E', // Green
  '#14B8A6', // Teal
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#6B7280', // Gray
  '#1F2937', // Dark
]

export const CategoryManagementDialog: React.FC<CategoryManagementDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { categories, refreshCategories } = useCategoryContext()
  const [editingCategory, setEditingCategory] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [formData, setFormData] = useState<Category>({
    name: '',
    color: '#3B82F6',
    icon: 'circle',
  })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      // Reset form when dialog closes
      setEditingCategory(null)
      setIsCreating(false)
      setShowColorPicker(false)
      setFormData({ name: '', color: '#3B82F6', icon: 'circle' })
      setError(null)
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleStartCreate = () => {
    setIsCreating(true)
    setEditingCategory(null)
    setShowColorPicker(false)
    setFormData({ name: '', color: '#3B82F6', icon: 'circle' })
    setError(null)
  }

  const handleStartEdit = (categoryName: string) => {
    const category = categories[categoryName]
    if (category) {
      setEditingCategory(categoryName)
      setIsCreating(false)
      setShowColorPicker(false)
      setFormData({
        name: category.name,
        color: category.color,
        icon: category.icon || 'circle',
      })
      setError(null)
    }
  }

  const handleCancelEdit = () => {
    setEditingCategory(null)
    setIsCreating(false)
    setShowColorPicker(false)
    setFormData({ name: '', color: '#3B82F6', icon: 'circle' })
    setError(null)
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Category name is required')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // If editing, update registry and update all classes currently in this category
      if (editingCategory) {
        // Update registry (supports rename)
        await apiService.updateCategory(editingCategory, formData)

        const oldCategory = categories[editingCategory]
        const classesToUpdate = oldCategory.classes

        // Update category for all classes
        await Promise.all(
          classesToUpdate.map((classId) =>
            apiService.setClassCategory(classId, formData)
          )
        )
      } else if (isCreating) {
        // Create category in registry so it appears even with 0 classes
        await apiService.createCategory(formData)
      }

      // Refresh categories (force refresh to bypass cache)
      await refreshCategories(true)

      // Reset form
      handleCancelEdit()
    } catch (err) {
      console.error('Failed to save category:', err)
      setError('Failed to save category')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (categoryName: string) => {
    if (!confirm(`Delete category "${categoryName}"? Classes will become uncategorized.`)) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const category = categories[categoryName]
      const classesToUpdate = category.classes

      // Remove category from all classes
      await Promise.all(
        classesToUpdate.map((classId) => apiService.removeClassCategory(classId))
      )

      // Remove from registry
      await apiService.deleteCategory(categoryName)

      // Refresh categories (force refresh to bypass cache)
      await refreshCategories(true)
    } catch (err) {
      console.error('Failed to delete category:', err)
      setError('Failed to delete category')
    } finally {
      setLoading(false)
    }
  }

  const categoryList = Object.values(categories)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
            <p className="text-sm text-gray-500">Organize your classes into groups</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Create/Edit Form */}
          {(isCreating || editingCategory) ? (
            <div className="p-6">
              <div className="space-y-5">
                {/* Name Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all text-gray-900 placeholder-gray-400"
                    placeholder="e.g., Bracelets, Rings, Watches..."
                    autoFocus
                  />
                </div>

                {/* Color Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color
                  </label>

                  {/* Color Preview & Presets */}
                  <div className="flex items-center gap-3 mb-3">
                    <button
                      onClick={() => setShowColorPicker(!showColorPicker)}
                      className="w-10 h-10 rounded-xl border-2 border-gray-200 shadow-sm flex items-center justify-center transition-all hover:scale-105"
                      style={{ backgroundColor: formData.color }}
                      title="Open color picker"
                    >
                      <Palette className="h-4 w-4 text-white drop-shadow" />
                    </button>

                    <div className="flex gap-1.5 flex-wrap flex-1">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => setFormData({ ...formData, color })}
                          className={`w-7 h-7 rounded-lg transition-all hover:scale-110 ${
                            formData.color === color
                              ? 'ring-2 ring-offset-2 ring-gray-400'
                              : ''
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Expandable Color Picker */}
                  {showColorPicker && (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <HexColorPicker
                        color={formData.color}
                        onChange={(color) => setFormData({ ...formData, color })}
                        style={{ width: '100%', height: 160 }}
                      />
                      <div className="mt-3 flex items-center gap-2">
                        <input
                          type="text"
                          value={formData.color}
                          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                          placeholder="#3B82F6"
                        />
                        <div
                          className="w-10 h-10 rounded-lg border border-gray-200"
                          style={{ backgroundColor: formData.color }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Icon Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon
                  </label>
                  <div className="flex gap-2">
                    {AVAILABLE_ICONS.map(({ name, Icon }) => (
                      <button
                        key={name}
                        onClick={() => setFormData({ ...formData, icon: name })}
                        className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
                          formData.icon === name
                            ? 'bg-gray-900 shadow-lg scale-105'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        <Icon
                          className="h-5 w-5"
                          style={{ color: formData.icon === name ? formData.color : '#6B7280' }}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    {isCreating ? 'Create Category' : 'Save Changes'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6">
              {/* Create Button */}
              <button
                onClick={handleStartCreate}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-gray-300 hover:text-gray-700 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 font-medium"
              >
                <Plus className="h-4 w-4" />
                Add New Category
              </button>

              {/* Category List */}
              {categoryList.length > 0 && (
                <div className="mt-4 space-y-2">
                  {categoryList.map((category) => {
                    const IconComponent =
                      AVAILABLE_ICONS.find((i) => i.name === category.icon)?.Icon || Circle

                    return (
                      <div
                        key={category.name}
                        className="group flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-all"
                      >
                        {/* Color & Icon */}
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${category.color}15` }}
                        >
                          <IconComponent
                            className="h-5 w-5"
                            style={{ color: category.color }}
                          />
                        </div>

                        {/* Name and count */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {category.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {category.classCount} {category.classCount === 1 ? 'class' : 'classes'}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleStartEdit(category.name)}
                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(category.name)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors text-gray-500 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Empty State */}
              {categoryList.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No categories yet</p>
                  <p className="text-xs mt-1">Create one to organize your classes</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CategoryManagementDialog
